import { insidePolygon } from './geometry.js';
import { CONTENT } from '../content/runtime.js';
import { stationPad as placedStationPad, seatFacing, at } from '../content/placement.js';
import type { Family, GameConfig, Point, PadKind } from './types.js';

// JSON is validated at build time; runtime consumers use the generated payload directly.
export const FAMILIES = CONTENT.families as Family[];
export const CONFIG = CONTENT.config as GameConfig;
export const LANDSIDE_TOP = CONTENT.layout.LANDSIDE_TOP;
export const ENTRANCE = CONTENT.layout.ENTRANCE;
export const ENTRANCE_DOOR = CONTENT.layout.ENTRANCE_DOOR;
export const STREET_ENTRY = CONTENT.layout.STREET_ENTRY;
export const SPAWN = CONTENT.layout.SPAWN;
export const CHECK_IN_BOARD = CONTENT.layout.CHECK_IN_BOARD;
export const WAITING_BOARD = CONTENT.layout.WAITING_BOARD;
export const BOARD = CONTENT.layout.BOARD;
export const SECURITY_GATE = CONTENT.layout.SECURITY_GATE;
export const SECURITY_WALK = CONTENT.layout.SECURITY_WALK;
export const RACK = CONTENT.layout.RACK;
export const BELT = CONTENT.layout.BELT;
export const KIOSK = CONTENT.layout.KIOSK;
export const ADMIN = CONTENT.layout.ADMIN;
export const ADMIN_DESK = CONTENT.layout.ADMIN_DESK;
export const RADIO_PAD = CONTENT.layout.RADIO_PAD;
export const LOADING_PAD = CONTENT.layout.LOADING_PAD;
export const RACK_PICKUP = CONTENT.layout.RACK_PICKUP;
export const TOWER = CONTENT.layout.TOWER;
export const TOWER_ART = CONTENT.layout.TOWER_ART;
export const CREW = CONTENT.layout.CREW;
export const OPERATIONS = CONTENT.layout.OPERATIONS;
export const SPECIAL = CONTENT.layout.SPECIAL as {kind:PadKind;x:number;y:number;label:string}[];
export const ROUTE = CONTENT.layout.ROUTE;
export const GATES = CONTENT.layout.GATES;
export const VENDING = CONTENT.layout.VENDING;
export const BINS = CONTENT.layout.BINS;
export const STATION_FOOTPRINTS = CONTENT.layout.STATION_FOOTPRINTS;
export const WORK_PAD = CONTENT.layout.WORK_PAD;
export const ACTION_PAD = CONTENT.layout.ACTION_PAD;
export const CHECK_IN_DESKS = CONTENT.layout.CHECK_IN_DESKS;
export const COUNTERS = CONTENT.layout.COUNTERS;
export const SOFAS = CONTENT.layout.SOFAS;
export const FURNITURE = CONTENT.layout.FURNITURE;
export const DIVIDER = CONTENT.layout.DIVIDER;
export const GATE_WALLS = CONTENT.layout.GATE_WALLS;
export const BOUNDARIES = CONTENT.layout.BOUNDARIES;
export const TERMINAL_PROPS = CONTENT.layout.TERMINAL_PROPS;
export const TROLLEYS = CONTENT.layout.TROLLEYS;
export const PLANTERS = CONTENT.layout.PLANTERS;
export const OBSTACLES = CONTENT.layout.OBSTACLES;
export const CORE_STAGES = CONTENT.layout.CORE_STAGES;
export const PASSENGER_WALK_SPEED = CONTENT.balance.constants.PASSENGER_WALK_SPEED;
export const CAPTAIN_WALK_SPEED = CONTENT.balance.constants.CAPTAIN_WALK_SPEED;
export const CAPTAIN_ENTRY_SPEED = CONTENT.balance.constants.CAPTAIN_ENTRY_SPEED;
export const SMALL_COOLDOWN = CONTENT.balance.constants.SMALL_COOLDOWN;
export const FACILITY_COOLDOWN = CONTENT.balance.constants.FACILITY_COOLDOWN;
export const QUEUE_PATIENCE = CONTENT.balance.constants.QUEUE_PATIENCE;
export const ANGRY_PATIENCE = CONTENT.balance.constants.ANGRY_PATIENCE;
export const LEAVING_WINDOW = CONTENT.balance.constants.LEAVING_WINDOW;
export const SAD_RADIUS = CONTENT.balance.constants.SAD_RADIUS;
export { bounds } from './geometry.js';
export { seatFacing };

export function stationPad(index: number, kind: PadKind): Point {
    return placedStationPad(CONFIG.stages, STATION_FOOTPRINTS, index, kind);
}

/**
 * A desk's takings pile up beside it, turned with the desk: off a counter end first, then the staff-side corners, then
 * further along the counter line (a row of check-in desks). The first spot clear of props, walls, pads and the security
 * walk wins, and it stays out of reach of the desk's work pad, so working the desk never picks the cash up.
 */
const cashSpots = new Map<string, Point>();
export function cashPoint(index: number): Point {
    const s = CONFIG.stages[index], { w, h } = STATION_FOOTPRINTS[index];
    const key = `${index}:${s.x},${s.y},${s.facing ?? ''}`;
    const known = cashSpots.get(key);
    if (known) return { ...known };
    const end = w / 2 + 30, back = -h / 2 - 20;
    const local = [[end, 0], [-end, 0], [end, back], [-end, back], ...[1, 2, 3, 4].flatMap(k => [[end + 42 * k, 0], [-end - 42 * k, 0]])];
    const spots = local.map(([side, out]) => at(s, side, out));
    const work = stationPad(index, 'work'), outline = (CONTENT.layout as { OUTLINE?: number[] }).OUTLINE;
    const pads = [...CONFIG.stages.flatMap((_, i) => (['build', 'hire', 'upgrade', 'divider'] as PadKind[]).map(kind => stationPad(i, kind))), ...SPECIAL, ...SECURITY_WALK];
    const clear = (p: Point) => (!outline || insidePolygon(outline, p.x, p.y))
        && Math.hypot(p.x - work.x, p.y - work.y) > 62
        && [...OBSTACLES, ...BOUNDARIES, ...GATE_WALLS].every(o => Math.abs(o.x - p.x) > o.w / 2 + 16 || Math.abs(o.y - p.y) > o.h / 2 + 16)
        && pads.every(q => Math.hypot(q.x - p.x, q.y - p.y) > 50);
    const spot = spots.find(clear) ?? spots[0];
    cashSpots.set(key, spot);
    return { ...spot };
}

export function walkableAirportPoint(point: Point, maxX: number, outline?: readonly number[]): boolean {
    // An airport with its own outline has no floor outside it.
    if (outline) return Number.isFinite(point.x + point.y) && insidePolygon(outline, point.x, point.y) && point.x <= maxX - 45;
    return Number.isFinite(point.x + point.y) && point.x >= 45 && point.x <= maxX - 45 && point.y >= 359 && point.y <= CONFIG.height - 43;
}

export function onPad(kind: string, at: Point, point: Point) {
    const size = kind === 'work' ? WORK_PAD : ACTION_PAD;
    return Math.abs(at.x - point.x) <= size.w / 2 && Math.abs(at.y - point.y) <= size.h / 2;
}

export function binPad(point: Point): Point { return { x: point.x, y: point.y + 44 }; }

export function vendingPoint(index: number): Point { const v = VENDING[index]; return { x: v.x, y: v.y + 68 }; }

export function vendingUpgradePoint(index: number): Point { const v = VENDING[index]; return { x: v.x, y: v.y + 134 }; }

export function stageServing(i: number, s?: { unlocked: boolean; manager?: boolean; assignedWorkers?: number; activeWorkers?: number }): boolean {
    return !!s?.unlocked && (CORE_STAGES.includes(i) || (s.assignedWorkers === undefined ? s.manager !== false : s.assignedWorkers > 0) || (s.activeWorkers ?? 0) > 0);
}

/** MGT-009: Passport Control is mandatory for international travellers (even unstaffed: they wait) and skipped by domestic ones. */
export const PASSPORT = 3;
export const DUTY_FREE = 4, LOUNGE = 5, DISPATCH = 7;
/** What a traveller may do on the way: Passport (international), a shop visit (enough dwell time), premium lounge (VIP). */
export interface TravellerRoute { international?: boolean; retail?: boolean; premium?: boolean; /** Act I: visit every staffed shop, no dwell rule. */ legacy?: boolean }
/**
 * MGT-009/010 routing. International travellers must pass Passport; domestic skip it. Shops share one budget: a
 * traveller with enough dwell visits at most one of Duty Free and the Sky Lounge (VIPs prefer a staffed lounge, others
 * shop or buy a lounge day pass); a rushed traveller skips both.
 */
export function nextStage(current: number, stations: { unlocked: boolean; manager?: boolean; activeWorkers?: number }[], traveller: boolean | TravellerRoute = false): number {
    const t: TravellerRoute = typeof traveller === 'boolean' ? { international: traveller, legacy: true } : traveller;
    const start = ROUTE.indexOf(current as typeof ROUTE[number]);
    if (t.legacy) return ROUTE.slice(start + 1).find(i => i === PASSPORT ? !!t.international && !!stations[i]?.unlocked : stageServing(i, stations[i])) ?? -1;
    const shop = t.retail === false || current === DUTY_FREE || current === LOUNGE ? -1
        : t.premium && stageServing(LOUNGE, stations[LOUNGE]) ? LOUNGE : stageServing(DUTY_FREE, stations[DUTY_FREE]) ? DUTY_FREE : stageServing(LOUNGE, stations[LOUNGE]) ? LOUNGE : -1;
    // Flight Dispatch is a back-office job from Act II (MGT-011): passengers never queue there.
    return ROUTE.slice(start + 1).find(i => i === PASSPORT ? !!t.international && !!stations[i]?.unlocked
        : i === DUTY_FREE || i === LOUNGE ? i === shop : i !== DISPATCH && stageServing(i, stations[i])) ?? -1;
}
/** Where Passport Control is open, three in ten travellers are international (derived from the serial, so never stored). */
export function internationalTraveller(serial: number, stations: { unlocked: boolean }[]): boolean {
    return !!stations[PASSPORT]?.unlocked && serial % 10 < 3;
}

